import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/companies/FormField";
import { useLanguage } from "@/contexts/LanguageContext";
import { FormData } from "@/pages/CompanyForm";
import {
  Shield,
  Mail,
  Lock,
  Key,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  RefreshCw,
  Send,
  Edit2,
  Edit,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { PasswordChangeDialog, EmailChangeDialog, AccountStatusBadge } from "./account";

interface ValidationField {
  isValid: boolean;
  error: string;
  touched: boolean;
}

interface AccountAccessSectionProps {
  isEditMode: boolean;
  formData: FormData;
  formInitialized: boolean;
  validation: Record<keyof FormData, ValidationField>;
  claims: any;
  user: any;
  isSubmitting: boolean;
  isSendingInvitation: boolean;
  users: { id: string; email: string }[];
  usersLoading?: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputBlur: (fieldName: keyof FormData) => void;
  handleCreateAccount: () => Promise<void>;
  handleChangeEmail: (newEmail: string) => Promise<void>;
  handlePasswordChange?: (currentPassword: string, newPassword: string) => Promise<boolean>;
}

export const AccountAccessSection = ({
  formData,
  validation,
  claims,
  isSubmitting,
  isSendingInvitation,
  users,
  usersLoading,
  handleInputChange,
  handleInputBlur,
  handleCreateAccount,
  handleChangeEmail,
  handlePasswordChange,
}: AccountAccessSectionProps) => {
  const { t } = useLanguage();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isConfirmingPasswordChange, setIsConfirmingPasswordChange] = useState(false);
  const [isConfirmingEmailChange, setIsConfirmingEmailChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [newEmailAddress, setNewEmailAddress] = useState("");
  const [passwordTouched, setPasswordTouched] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const { toast } = useToast();

  const handlePasswordFieldChange = (field: string, value: string) => {
    if (field === "current") {
      setCurrentPassword(value);
    } else if (field === "new") {
      setNewPassword(value);
      if (passwordError === t("validation.company.password.mismatch")) {
        setPasswordError("");
      }
    } else if (field === "confirm") {
      setConfirmPassword(value);
      if (passwordError === t("validation.company.password.mismatch")) {
        setPasswordError("");
      }
    }
  };

  const handlePasswordBlur = (field: string) => {
    setPasswordTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handlePasswordSubmit = async () => {
    if (!currentPassword) {
      setPasswordError(t("validation.company.password.currentRequired"));
      return;
    }

    if (!newPassword) {
      setPasswordError(t("validation.company.password.required"));
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t("validation.company.password.minLength"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("validation.company.password.mismatch"));
      return;
    }

    try {
      if (handlePasswordChange) {
        const success = await handlePasswordChange(currentPassword, newPassword);
        if (success) {
          setIsChangingPassword(false);
          setIsConfirmingPasswordChange(false);
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setPasswordError("");
        }
      } else {
        setIsConfirmingPasswordChange(false);
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("companyForm.password.changeError"),
        });
      }
    } catch (error: any) {
      console.error("Password change error:", error);

      setIsConfirmingPasswordChange(false);

      if (
        error?.code === "auth/invalid-credential" ||
        error?.message?.includes("invalid-credential") ||
        error?.message?.includes("invalid credential")
      ) {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("validation.company.password.incorrectCurrent"),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("error"),
          description: t("companyForm.password.changeError"),
        });
      }
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 rounded-lg">
            <Shield className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {claims?.role === "company" ? t("companyForm.account.title") : t("companyForm.account.access")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{t("companyForm.account.description")}</p>
          </div>
        </div>
        <AccountStatusBadge accountState={formData.accountState} />
      </div>

      {claims?.role === "company" ? (
        /* Company user view */
        <div className="space-y-6">
          {/* Account Info Card */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg">
                  <Mail className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{t("companyForm.account.emailAddress")}</h3>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="w-[200px] text-sm">{t("companyForm.account.emailTooltip")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-gray-600 mt-1">{formData.accountState.email}</p>
                </div>
              </div>
              {formData.accountState.isRegistered && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsConfirmingEmailChange(true)}
                  disabled={isSendingInvitation}
                  className="flex items-center gap-2 border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                >
                  <Edit className="h-4 w-4" />
                  {t("companyForm.buttons.changeEmail")}
                </Button>
              )}
            </div>
          </div>

          {/* Password Management */}
          {formData.accountState.isRegistered && (
            <div className="space-y-4">
              {isChangingPassword ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
                  <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                    <div className="p-2 bg-teal-50 rounded-lg">
                      <Key className="h-5 w-5 text-teal-600" />
                    </div>
                    <h3 className="font-medium text-gray-900">{t("companyForm.password.changeTitle")}</h3>
                  </div>

                  <div className="space-y-4">
                    <FormField
                      label={t("companyForm.password.current")}
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => handlePasswordFieldChange("current", e.target.value)}
                      placeholder={t("companyForm.password.currentPlaceholder")}
                      validation={{ isValid: true, error: "", touched: passwordTouched.current }}
                      onBlur={() => handlePasswordBlur("current")}
                    />

                    <FormField
                      label={t("companyForm.password.new")}
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => handlePasswordFieldChange("new", e.target.value)}
                      placeholder={t("companyForm.password.newPlaceholder")}
                      validation={{ isValid: true, error: "", touched: passwordTouched.new }}
                      onBlur={() => handlePasswordBlur("new")}
                    />

                    <FormField
                      label={t("companyForm.password.confirm")}
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => handlePasswordFieldChange("confirm", e.target.value)}
                      placeholder={t("companyForm.password.confirmPlaceholder")}
                      validation={{ isValid: true, error: "", touched: passwordTouched.confirm }}
                      onBlur={() => handlePasswordBlur("confirm")}
                    />

                    {/* Show real-time password match feedback */}
                    {newPassword && confirmPassword && (
                      <div className="mt-1 mb-2">
                        {newPassword === confirmPassword ? (
                          <div className="flex items-center gap-1 text-xs text-teal-600">
                            <CheckCircle2 className="h-3 w-3" />
                            <span>{t("validation.company.password.match")}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <AlertTriangle className="h-3 w-3" />
                            <span>{t("validation.company.password.mismatch")}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Show error message alert only for errors other than password mismatch */}
                    {passwordError && passwordError !== t("validation.company.password.mismatch") && (
                      <Alert variant="destructive">
                        <AlertDescription>{passwordError}</AlertDescription>
                      </Alert>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsChangingPassword(false);
                          setCurrentPassword("");
                          setNewPassword("");
                          setConfirmPassword("");
                          setPasswordError("");
                        }}
                      >
                        {t("companyForm.buttons.cancel")}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setIsConfirmingPasswordChange(true)}
                        disabled={
                          !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword
                        }
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        {t("companyForm.password.update")}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg">
                      <Lock className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{t("companyForm.password.security")}</h3>
                      <p className="text-sm text-gray-500 mt-1">{t("companyForm.password.securityInfo")}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsChangingPassword(true)}
                    className="flex items-center gap-2 border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                  >
                    <Key className="h-4 w-4" />
                    {t("companyForm.password.change")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Admin view */
        <div className="space-y-6">
          {!formData.accountState.hasAssociatedAccount && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="p-2 bg-teal-50 rounded-lg">
                  <Mail className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{t("companyForm.account.addEmail")}</h3>
                  <p className="text-sm text-gray-500 mt-1">{t("companyForm.account.addEmailDescription")}</p>
                </div>
              </div>

              <FormField
                label={t("companyForm.fields.email")}
                id="accountState.email"
                type="email"
                value={formData.accountState.email}
                onChange={handleInputChange}
                onBlur={() => handleInputBlur("accountState")}
                validation={{
                  isValid: validation.accountState?.isValid || false,
                  error: validation.accountState?.error || "",
                  touched: validation.accountState?.touched || false,
                }}
                required={false}
              />

              <Button
                type="button"
                onClick={handleCreateAccount}
                disabled={isSendingInvitation || !formData.accountState.email}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center gap-2"
              >
                {isSendingInvitation ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    {t("companyForm.buttons.sendingInvitation")}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {t("companyForm.buttons.sendInvitation")}
                  </>
                )}
              </Button>
            </div>
          )}

          {formData.accountState.hasAssociatedAccount && !formData.accountState.isRegistered && (
            <div className="space-y-4">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertDescription className="text-amber-800">
                  {t("companyForm.account.pendingRegistrationInfo")}
                </AlertDescription>
              </Alert>

              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
                <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                  <div className="p-2 bg-teal-50 rounded-lg">
                    <Mail className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{t("companyForm.account.manageEmail")}</h3>
                    <p className="text-sm text-gray-500 mt-1">{formData.accountState.email}</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCreateAccount}
                    disabled={isSendingInvitation}
                    className="flex-1 flex items-center justify-center gap-2 border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                  >
                    {isSendingInvitation ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        {t("companyForm.buttons.resendingInvitation")}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        {t("companyForm.buttons.resendInvitation")}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsConfirmingEmailChange(true)}
                    disabled={isSendingInvitation}
                    className="flex-1 flex items-center justify-center gap-2 border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                  >
                    <Edit2 className="h-4 w-4" />
                    {t("companyForm.buttons.changeEmail")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {formData.accountState.hasAssociatedAccount && formData.accountState.isRegistered && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="p-2 bg-teal-50 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{t("companyForm.account.registeredAccount")}</h3>
                  <p className="text-sm text-gray-500 mt-1">{formData.accountState.email}</p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => setIsConfirmingEmailChange(true)}
                disabled={isSendingInvitation}
                className="w-full flex items-center justify-center gap-2 border-teal-200 hover:bg-teal-50 hover:text-teal-700"
              >
                <Edit2 className="h-4 w-4" />
                {t("companyForm.buttons.changeEmail")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Password Change Dialog */}
      <PasswordChangeDialog
        isOpen={isConfirmingPasswordChange}
        onOpenChange={setIsConfirmingPasswordChange}
        currentPassword={currentPassword}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        passwordError={passwordError}
        passwordTouched={passwordTouched}
        isSubmitting={isSubmitting}
        onPasswordFieldChange={handlePasswordFieldChange}
        onPasswordBlur={handlePasswordBlur}
        onPasswordSubmit={handlePasswordSubmit}
      />

      {/* Email Change Dialog */}
      <EmailChangeDialog
        isOpen={isConfirmingEmailChange}
        onOpenChange={setIsConfirmingEmailChange}
        currentEmail={formData.accountState.email}
        newEmailAddress={newEmailAddress}
        isSendingInvitation={isSendingInvitation}
        onNewEmailChange={setNewEmailAddress}
        onEmailChangeSubmit={handleChangeEmail}
        users={users}
        usersLoading={usersLoading}
      />
    </div>
  );
};
